import { z } from 'zod';

/** Accepted uploads (BB-52): raster formats only; SVG is not accepted because the SDKs do not render it in push or cards. */
export const MEDIA_MIME_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'] as const;
export const MediaMimeType = z.enum(MEDIA_MIME_TYPES);
export type MediaMimeType = z.infer<typeof MediaMimeType>;

export const MEDIA_MAX_BYTES = 5 * 1024 * 1024;
export const MEDIA_MAX_DIMENSION = 8000;

/**
 * Renditions every version gets: the aspect is fixed for crops (`null` keeps the original aspect and only scales
 * down), `width` is the target width in pixels.
 */
export const RENDITION_PRESETS = {
  thumb: { label: 'Library thumbnail', aspect: null, width: 320 },
  card_banner_2_96: { label: 'Content Card banner (2.96:1)', aspect: 2.96, width: 1065 },
  push_1_1: { label: 'Push image (1:1)', aspect: 1, width: 512 },
  push_2_1: { label: 'Push big picture (2:1)', aspect: 2, width: 1024 },
  iam_full: { label: 'In-app message (original aspect)', aspect: null, width: 1200 },
} as const satisfies Record<string, { label: string; aspect: number | null; width: number }>;
export type RenditionPreset = keyof typeof RENDITION_PRESETS;
export const RenditionPreset = z.enum(Object.keys(RENDITION_PRESETS) as [RenditionPreset, ...RenditionPreset[]]);

export const RenditionStatus = z.enum(['pending', 'ready', 'skipped', 'failed']);
export type RenditionStatus = z.infer<typeof RenditionStatus>;

/** Crop rectangle in pixels of the original version. */
export const Crop = z.strictObject({
  x: z.number().int().min(0),
  y: z.number().int().min(0),
  width: z.number().int().min(1),
  height: z.number().int().min(1),
});
export type Crop = z.infer<typeof Crop>;

export const MediaListQuery = z.object({
  query: z.string().trim().max(255).optional(),
  tag: z.string().trim().max(64).optional(),
  type: MediaMimeType.optional(),
  archived: z.enum(['true', 'false']).default('false'),
  cursor: z.uuid().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type MediaListQuery = z.infer<typeof MediaListQuery>;

/** Upload query: the file goes as the raw request body with its image content type. */
export const MediaUploadQuery = z.object({
  name: z.string().trim().min(1).max(255),
  altText: z.string().trim().max(500).optional(),
});

export const UpdateMediaBody = z
  .strictObject({ name: z.string().trim().min(1).max(255).optional(), altText: z.string().trim().max(500).nullable().optional() })
  .refine((b) => Object.keys(b).length > 0, 'nothing to change');
export type UpdateMediaBody = z.infer<typeof UpdateMediaBody>;

export const SetCropBody = z.strictObject({ crop: Crop });

/** How content refers to an image: a fixed version, optionally a rendition; the renderer resolves it to a URL. */
export const MediaRef = z.strictObject({ assetId: z.uuid(), versionId: z.uuid(), rendition: RenditionPreset.optional() });
export type MediaRef = z.infer<typeof MediaRef>;
